import React, { useState, useMemo } from "react";
import { IoClose } from "react-icons/io5";
import toast from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";

const MergePrescriptionsModal = ({ selectedPrescriptions, admissionSelected, setShowPrescriptionMergeModal, setSelectedPrescriptions, getAdmissionDetails }) => {
  const [primaryPrescriptionId, setPrimaryPrescriptionId] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedPrescriptionsList = useMemo(() => {
    return admissionSelected?.prescriptions?.filter((prescription) => selectedPrescriptions.has(prescription._id)) || [];
  }, [selectedPrescriptions, admissionSelected?.prescriptions]);

  const formatDate = (date) => {
    if (!date) return "Non renseignée";
    if (typeof date === "string") return date;
    if (date instanceof Date) return date.toLocaleDateString("fr-FR");
    return "Non renseignée";
  };

  const mergePrescriptions = async () => {
    if (!primaryPrescriptionId) return;

    if (window.confirm("Voulez-vous vraiment fusionner ces prescriptions ? L'opération est irréversible.") === false) return;

    setLoading(true);
    try {
      const secondaryPrescriptions = selectedPrescriptionsList.filter((p) => p._id !== primaryPrescriptionId);

      const response = await API.post(`/admin/admission/${admissionSelected._id}/prescription/${primaryPrescriptionId}/merge`, {
        secondaryPrescriptionIds: secondaryPrescriptions.map((p) => p._id),
      });

      if (!response.ok) throw new Error(response.message || "Erreur lors de la fusion");

      toast.success(`${secondaryPrescriptions.length} prescription(s) fusionnée(s) avec succès`);
      getAdmissionDetails(admissionSelected._id);
      setSelectedPrescriptions(new Set());
      setShowPrescriptionMergeModal(false);
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors de la fusion des prescriptions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-medium">Fusionner les prescriptions</h2>
          <button onClick={() => setShowPrescriptionMergeModal(false)} className="text-gray-400 hover:text-gray-600">
            <IoClose className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <h3 className="font-medium mb-2">Sélectionnez la prescription principale</h3>
          <p className="text-sm text-gray-600 mb-4">Les autres prescriptions seront fusionnées dans celle-ci.</p>

          <div className="space-y-3">
            {selectedPrescriptionsList.map((prescription, index) => (
              <div
                key={prescription._id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  primaryPrescriptionId === prescription._id ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setPrimaryPrescriptionId(prescription._id)}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    checked={primaryPrescriptionId === prescription._id}
                    onChange={() => setPrimaryPrescriptionId(prescription._id)}
                    className="mt-1 text-primary-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Prescription {index + 1}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Date:</span> {formatDate(prescription.date)}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Médecin:</span> {prescription.doctor?.firstName} {prescription.doctor?.lastName || "Non renseigné"}
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">{prescription.acts?.length || 0}</span> acte(s)
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => setShowPrescriptionMergeModal(false)}
            disabled={loading}
          >
            Fermer
          </button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={loading}
            onClick={mergePrescriptions}
          >
            {loading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
            Confirmer la fusion
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergePrescriptionsModal;
